import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Check, 
  X, 
  Eye, 
  EyeOff,
  Upload,
  Phone,
  Calendar,
  Star,
  Image as ImageIcon,
  Scissors,
  Settings as SettingsIcon,
  MessageSquare
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

// Dashboard Stats Component
function DashboardStats({ stats }) {
  const statCards = [
    { label: "Services", value: stats.services, icon: Scissors, color: "bg-blue-500" },
    { label: "Gallery Items", value: stats.gallery, icon: ImageIcon, color: "bg-purple-500" },
    { label: "Reviews", value: stats.reviews, icon: Star, color: "bg-yellow-500" },
    { label: "Bookings", value: stats.bookings, icon: Calendar, color: "bg-green-500" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      {statCards.map((stat) => (
        <div key={stat.label} className="bg-white rounded-lg p-6 shadow-sm border">
          <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-4`}>
            <stat.icon size={20} className="text-white" />
          </div>
          <p className="text-3xl font-semibold">{stat.value}</p>
          <p className="text-sm text-neutral-500">{stat.label}</p>
        </div>
      ))}
    </div>
  );
}

// Services Management
function ServicesManager() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [newService, setNewService] = useState({
    name: "", category: "Hair", price: "", duration: "", description: "", image: ""
  });

  const categories = ["Hair", "Color", "Spa", "Skin", "Bridal", "Grooming"];

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API}/services`);
      setServices(res.data);
    } catch (error) {
      toast.error("Failed to fetch services");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newService.name) return toast.error("Name is required");
    try {
      await axios.post(`${API}/services`, newService, getAuthHeader());
      toast.success("Service created");
      setNewService({ name: "", category: "Hair", price: "", duration: "", description: "", image: "" });
      fetchServices();
    } catch (error) {
      toast.error("Failed to create service");
    }
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(`${API}/services/${id}`, editing, getAuthHeader());
      toast.success("Service updated");
      setEditing(null);
      fetchServices();
    } catch (error) {
      toast.error("Failed to update service");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await axios.delete(`${API}/services/${id}`, getAuthHeader());
      toast.success("Service deleted");
      fetchServices();
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <div data-testid="services-manager">
      <h2 className="text-xl font-semibold mb-4">Services ({services.length})</h2>
      
      {/* Add New Service */}
      <div className="bg-white p-4 rounded-lg border mb-6">
        <h3 className="font-medium mb-4">Add New Service</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            placeholder="Service name"
            value={newService.name}
            onChange={(e) => setNewService({ ...newService, name: e.target.value })}
            data-testid="new-service-name"
          />
          <Select
            value={newService.category}
            onValueChange={(v) => setNewService({ ...newService, category: v })}
          >
            <SelectTrigger data-testid="new-service-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            placeholder="Price (e.g., ₹500+)"
            value={newService.price}
            onChange={(e) => setNewService({ ...newService, price: e.target.value })}
          />
          <Input
            placeholder="Duration (e.g., 1 hr)"
            value={newService.duration}
            onChange={(e) => setNewService({ ...newService, duration: e.target.value })}
          />
          <Input
            placeholder="Image URL"
            value={newService.image}
            onChange={(e) => setNewService({ ...newService, image: e.target.value })}
          />
          <Input
            placeholder="Description"
            value={newService.description}
            onChange={(e) => setNewService({ ...newService, description: e.target.value })}
          />
        </div>
        <Button onClick={handleCreate} className="mt-4" data-testid="add-service-btn">
          <Plus size={16} className="mr-2" /> Add Service
        </Button>
      </div>

      {/* Services List */}
      <div className="space-y-3">
        {services.map((service) => (
          <div key={service.id} className="bg-white p-4 rounded-lg border flex items-center gap-4">
            {service.image && (
              <img src={service.image} alt="" className="w-16 h-16 object-cover rounded" />
            )}
            <div className="flex-1">
              {editing?.id === service.id ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                  <Input
                    value={editing.price}
                    onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                  />
                  <Input
                    value={editing.duration}
                    onChange={(e) => setEditing({ ...editing, duration: e.target.value })}
                  />
                  <Select
                    value={editing.category}
                    onValueChange={(v) => setEditing({ ...editing, category: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  <p className="font-medium">{service.name}</p>
                  <p className="text-sm text-neutral-500">
                    {service.category} • {service.price} • {service.duration}
                  </p>
                </>
              )}
            </div>
            <div className="flex gap-2">
              {editing?.id === service.id ? (
                <>
                  <Button size="sm" variant="ghost" onClick={() => handleUpdate(service.id)}>
                    <Check size={16} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                    <X size={16} />
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...service })}>
                    <Edit2 size={16} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(service.id)}>
                    <Trash2 size={16} className="text-red-500" />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Gallery Management
function GalleryManager() {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({ url: "", caption: "", tags: "" });

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setGallery(res.data);
    } catch (error) {
      toast.error("Failed to fetch gallery");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newItem.url) return toast.error("URL is required");
    try {
      const data = {
        ...newItem,
        tags: newItem.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      await axios.post(`${API}/gallery`, data, getAuthHeader());
      toast.success("Gallery item added");
      setNewItem({ url: "", caption: "", tags: "" });
      fetchGallery();
    } catch (error) {
      toast.error("Failed to add gallery item");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, getAuthHeader());
      toast.success("Item deleted");
      fetchGallery();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <div data-testid="gallery-manager">
      <h2 className="text-xl font-semibold mb-4">Gallery ({gallery.length})</h2>
      
      {/* Add New Item */}
      <div className="bg-white p-4 rounded-lg border mb-6">
        <h3 className="font-medium mb-4">Add New Image</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            placeholder="Image URL"
            value={newItem.url}
            onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
            data-testid="new-gallery-url"
          />
          <Input
            placeholder="Caption"
            value={newItem.caption}
            onChange={(e) => setNewItem({ ...newItem, caption: e.target.value })}
          />
          <Input
            placeholder="Tags (comma separated)"
            value={newItem.tags}
            onChange={(e) => setNewItem({ ...newItem, tags: e.target.value })}
          />
        </div>
        <Button onClick={handleCreate} className="mt-4" data-testid="add-gallery-btn">
          <Plus size={16} className="mr-2" /> Add Image
        </Button>
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {gallery.map((item) => (
          <div key={item.id} className="relative group">
            <img
              src={item.url}
              alt={item.caption}
              className="w-full aspect-square object-cover rounded-lg"
            />
            <button
              onClick={() => handleDelete(item.id)}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Reviews Management
function ReviewsManager() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newReview, setNewReview] = useState({ name: "", rating: 5, text: "" });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews/all`, getAuthHeader());
      setReviews(res.data);
    } catch (error) {
      toast.error("Failed to fetch reviews");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newReview.name || !newReview.text) return toast.error("Name and text required");
    try {
      await axios.post(`${API}/reviews`, newReview, getAuthHeader());
      toast.success("Review added");
      setNewReview({ name: "", rating: 5, text: "" });
      fetchReviews();
    } catch (error) {
      toast.error("Failed to add review");
    }
  };

  const handleToggleApproval = async (id) => {
    try {
      await axios.put(`${API}/reviews/${id}/approve`, {}, getAuthHeader());
      toast.success("Review status updated");
      fetchReviews();
    } catch (error) {
      toast.error("Failed to update review");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/reviews/${id}`, getAuthHeader());
      toast.success("Review deleted");
      fetchReviews();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <div data-testid="reviews-manager">
      <h2 className="text-xl font-semibold mb-4">Reviews ({reviews.length})</h2>
      
      {/* Add New Review */}
      <div className="bg-white p-4 rounded-lg border mb-6">
        <h3 className="font-medium mb-4">Add New Review</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            placeholder="Customer name"
            value={newReview.name}
            onChange={(e) => setNewReview({ ...newReview, name: e.target.value })}
            data-testid="new-review-name"
          />
          <Select
            value={String(newReview.rating)}
            onValueChange={(v) => setNewReview({ ...newReview, rating: Number(v) })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[5, 4, 3, 2, 1].map((r) => (
                <SelectItem key={r} value={String(r)}>{r} Stars</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            placeholder="Review text"
            value={newReview.text}
            onChange={(e) => setNewReview({ ...newReview, text: e.target.value })}
            className="md:col-span-3"
          />
        </div>
        <Button onClick={handleCreate} className="mt-4" data-testid="add-review-btn">
          <Plus size={16} className="mr-2" /> Add Review
        </Button>
      </div>

      {/* Reviews List */}
      <div className="space-y-3">
        {reviews.map((review) => (
          <div key={review.id} className="bg-white p-4 rounded-lg border">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium">{review.name}</span>
                  <div className="flex gap-0.5">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  {!review.approved && (
                    <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Hidden</span>
                  )}
                </div>
                <p className="text-sm text-neutral-600">{review.text}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => handleToggleApproval(review.id)}>
                  {review.approved ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(review.id)}>
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Bookings Management
function BookingsManager() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API}/bookings`, getAuthHeader());
      setBookings(res.data);
    } catch (error) {
      toast.error("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkContacted = async (id) => {
    try {
      await axios.put(`${API}/bookings/${id}/contacted`, {}, getAuthHeader());
      toast.success("Marked as contacted");
      fetchBookings();
    } catch (error) {
      toast.error("Failed to update booking");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await axios.delete(`${API}/bookings/${id}`, getAuthHeader());
      toast.success("Booking deleted");
      fetchBookings();
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <div data-testid="bookings-manager">
      <h2 className="text-xl font-semibold mb-4">Bookings ({bookings.length})</h2>
      
      {bookings.length === 0 ? (
        <p className="text-neutral-500">No bookings yet</p>
      ) : (
        <div className="space-y-3">
          {bookings.map((booking) => (
            <div key={booking.id} className={`bg-white p-4 rounded-lg border ${booking.contacted ? 'opacity-60' : ''}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{booking.name}</span>
                    {booking.contacted && (
                      <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded">Contacted</span>
                    )}
                  </div>
                  <p className="text-sm text-neutral-600 flex items-center gap-2">
                    <Phone size={14} /> {booking.phone}
                  </p>
                  {booking.service && (
                    <p className="text-sm text-neutral-600">Service: {booking.service}</p>
                  )}
                  {booking.preferred_date && (
                    <p className="text-sm text-neutral-600">
                      Date: {booking.preferred_date} {booking.preferred_time && `at ${booking.preferred_time}`}
                    </p>
                  )}
                  {booking.notes && (
                    <p className="text-sm text-neutral-500 mt-2">Notes: {booking.notes}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {!booking.contacted && (
                    <Button size="sm" variant="outline" onClick={() => handleMarkContacted(booking.id)}>
                      <Check size={16} className="mr-1" /> Contacted
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(booking.id)}>
                    <Trash2 size={16} className="text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Settings Manager
function SettingsManager() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (error) {
      toast.error("Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, getAuthHeader());
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <div data-testid="settings-manager">
      <h2 className="text-xl font-semibold mb-4">Site Settings</h2>
      
      <div className="bg-white p-6 rounded-lg border space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Salon Name</label>
            <Input
              value={settings?.salon_name || ""}
              onChange={(e) => setSettings({ ...settings, salon_name: e.target.value })}
              data-testid="settings-salon-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tagline</label>
            <Input
              value={settings?.tagline || ""}
              onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Phone</label>
            <Input
              value={settings?.phone || ""}
              onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">WhatsApp</label>
            <Input
              value={settings?.whatsapp || ""}
              onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <Input
              value={settings?.email || ""}
              onChange={(e) => setSettings({ ...settings, email: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Working Hours</label>
            <Input
              value={settings?.hours || ""}
              onChange={(e) => setSettings({ ...settings, hours: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2">Address</label>
            <Textarea
              value={settings?.address || ""}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Instagram URL</label>
            <Input
              value={settings?.instagram || ""}
              onChange={(e) => setSettings({ ...settings, instagram: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Facebook URL</label>
            <Input
              value={settings?.facebook || ""}
              onChange={(e) => setSettings({ ...settings, facebook: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2">Hero Image URL</label>
            <Input
              value={settings?.hero_image || ""}
              onChange={(e) => setSettings({ ...settings, hero_image: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2">Hero Heading</label>
            <Input
              value={settings?.hero_heading || ""}
              onChange={(e) => setSettings({ ...settings, hero_heading: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2">Hero Subheading</label>
            <Textarea
              value={settings?.hero_subheading || ""}
              onChange={(e) => setSettings({ ...settings, hero_subheading: e.target.value })}
            />
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-settings-btn">
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}

// Main Admin Dashboard
export default function AdminDashboard() {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "dashboard";
  const [stats, setStats] = useState({ services: 0, gallery: 0, reviews: 0, bookings: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [services, gallery, reviews, bookings] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/gallery`),
          axios.get(`${API}/reviews`),
          axios.get(`${API}/bookings`, getAuthHeader()),
        ]);
        setStats({
          services: services.data.length,
          gallery: gallery.data.length,
          reviews: reviews.data.length,
          bookings: bookings.data.length,
        });
      } catch (error) {
        console.error("Failed to fetch stats");
      }
    };
    fetchStats();
  }, []);

  const renderContent = () => {
    switch (tab) {
      case "services":
        return <ServicesManager />;
      case "gallery":
        return <GalleryManager />;
      case "reviews":
        return <ReviewsManager />;
      case "bookings":
        return <BookingsManager />;
      case "settings":
        return <SettingsManager />;
      default:
        return (
          <div data-testid="admin-dashboard">
            <h1 className="text-2xl font-semibold mb-6">Dashboard</h1>
            <DashboardStats stats={stats} />
            <div className="bg-white p-6 rounded-lg border">
              <h3 className="font-medium mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button variant="outline" onClick={() => window.location.href = '/admin/dashboard?tab=services'}>
                  <Scissors size={16} className="mr-2" /> Manage Services
                </Button>
                <Button variant="outline" onClick={() => window.location.href = '/admin/dashboard?tab=gallery'}>
                  <ImageIcon size={16} className="mr-2" /> Manage Gallery
                </Button>
                <Button variant="outline" onClick={() => window.location.href = '/admin/dashboard?tab=bookings'}>
                  <Calendar size={16} className="mr-2" /> View Bookings
                </Button>
                <Button variant="outline" onClick={() => window.location.href = '/admin/dashboard?tab=settings'}>
                  <SettingsIcon size={16} className="mr-2" /> Site Settings
                </Button>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="lg:pl-8 pt-12 lg:pt-0">
      {renderContent()}
    </div>
  );
}
